import { Routes } from '@angular/router';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { HelpDeskComponent } from './dashboard/help-desk/help-desk.component';
import { NotFoundComponent } from './common/not-found/not-found.component';
import { StarterComponent } from './starter/starter.component';
import { AuthenticationComponent } from './authentication/authentication.component';
import { SignInComponent } from './authentication/sign-in/sign-in.component';
import { SignUpComponent } from './authentication/sign-up/sign-up.component';
import { ForgotPasswordComponent } from './authentication/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './authentication/reset-password/reset-password.component';
import { LockScreenComponent } from './authentication/lock-screen/lock-screen.component';
import { LogoutComponent } from './authentication/logout/logout.component';
import { ConfirmEmailComponent } from './authentication/confirm-email/confirm-email.component';
import { InternalErrorComponent } from './common/internal-error/internal-error.component';
import { BlankPageComponent } from './blank-page/blank-page.component';
import { MyProfileComponent } from './my-profile/my-profile.component';
import { MastersComponent } from './masters/masters.component';
import { MCompaniesComponent } from './masters/m-companies/m-companies.component';
import { MCreateCompanyComponent } from './masters/m-create-company/m-create-company.component';
import { MEditCompanyComponent } from './masters/m-edit-company/m-edit-company.component';
import { MBranchOfficesComponent } from './masters/m-branch-offices/m-branch-offices.component';
import { MCreateBranchOfficeComponent } from './masters/m-create-branch-office/m-create-branch-office.component';
import { MEditBranchOfficeComponent } from './masters/m-edit-branch-office/m-edit-branch-office.component';
import { MBrandsComponent } from './masters/m-brands/m-brands.component';
import { MCreateBrandComponent } from './masters/m-create-brand/m-create-brand.component';
import { MEditBrandComponent } from './masters/m-edit-brand/m-edit-brand.component';
import { MCompanyUsersComponent } from './masters/m-company-users/m-company-users.component';
import { MCreateCompanyUserComponent } from './masters/m-create-company-user/m-create-company-user.component';
import { MEditCompanyUserComponent } from './masters/m-edit-company-user/m-edit-company-user.component';
import { VulnerabilityComponent } from './vulnerability/vulnerability.component';
import { VAssessmentsComponent } from './vulnerability/v-assessments/v-assessments.component';
import { VCreateAssessmentComponent } from './vulnerability/v-create-assessment/v-create-assessment.component';
import { VEditAssessmentComponent } from './vulnerability/v-edit-assessment/v-edit-assessment.component';
import { MAssetsComponent } from './masters/m-assets/m-assets.component';
import { MCreateAssetComponent } from './masters/m-create-asset/m-create-asset.component';
import { MEditAssetComponent } from './masters/m-edit-asset/m-edit-asset.component';
import { VViewVulnerabilitiesComponent } from './vulnerability/v-view-vulnerabilities/v-view-vulnerabilities.component';
import { VCreateVulnerabilityComponent } from './vulnerability/v-create-vulnerability/v-create-vulnerability.component';
import { VEditVulnerabilityComponent } from './vulnerability/v-edit-vulnerability/v-edit-vulnerability.component';
import { VViewEvidencesComponent } from './vulnerability/v-view-evidences/v-view-evidences.component';
import { VManageVulnerabilityComponent } from './vulnerability/v-manage-vulnerability/v-manage-vulnerability.component';
import { AsmComponent } from './asm/asm.component';
import { AQuickScanHistoryComponent } from './asm/a-quick-scan-history/a-quick-scan-history.component';
import { AScanFindingsComponent } from './asm/a-scan-findings/a-scan-findings.component';
import { MfaLoginComponent } from './authentication/mfa-login/mfa-login.component';
import { ATargetsComponent } from './asm/a-targets/a-targets.component';
import { AVulnerabilitiesComponent } from './asm/a-vulnerabilities/a-vulnerabilities.component';
import { ReportsComponent } from './reports/reports.component';
import { InternalSurfaceComponent } from './internal-surface/internal-surface.component';
import { IInfrastructureComponent } from './internal-surface/i-infrastructure/i-infrastructure.component';
import { IActiveDirectoryComponent } from './internal-surface/i-active-directory/i-active-directory.component';

export const routes: Routes = [
    // Redirect empty path to 'authentication/sign-in'
    {path: '', pathMatch : 'full', redirectTo: 'authentication/sign-in'},

    // Redirect signed-in user to the 'starter'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'dashboard'},
    // Auth routes for guests
    {
        path: 'authentication',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: AuthenticationComponent,
        children: [
            {path: 'sign-in', component: SignInComponent},
            {path: 'mfa-login', component: MfaLoginComponent},
            {path: 'sign-up', component: SignUpComponent},
            {path: 'forgot-password', component: ForgotPasswordComponent},
            {path: 'reset-password', component: ResetPasswordComponent},
            {path: 'confirm-email', component: ConfirmEmailComponent}
        ]
    },
    // Auth routes for authenticated users
    {
        path: 'lock-screen',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LockScreenComponent
    },
    {
        path: 'logout',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LogoutComponent

    },
    {
        path: 'starter',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
         component: StarterComponent
    },
    {
        path: 'masters',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: MastersComponent,
        children: [
            {path: 'companies', component: MCompaniesComponent},
            {path: 'create-company', component: MCreateCompanyComponent},
            {path: 'edit-company/:id', component: MEditCompanyComponent},
            {path: 'branch-offices', component: MBranchOfficesComponent},
            {path: 'create-branch-office', component: MCreateBranchOfficeComponent},
            {path: 'edit-branch-office/:id', component: MEditBranchOfficeComponent},
            {path: 'brands', component: MBrandsComponent},
            {path: 'create-brand', component: MCreateBrandComponent},
            {path: 'edit-brand/:id', component: MEditBrandComponent},
            {path: 'company-users', component: MCompanyUsersComponent},
            {path: 'create-company-user', component: MCreateCompanyUserComponent},
            {path: 'edit-company-user/:id/company/:companyId', component: MEditCompanyUserComponent},
            {path: 'assets', component: MAssetsComponent},
            {path: 'create-asset', component: MCreateAssetComponent},
            {path: 'edit-asset/:id', component: MEditAssetComponent}
        ]
    },
    {
        path: 'vulnerability',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: VulnerabilityComponent,
        children: [
            {path: 'assessments', component: VAssessmentsComponent},
            {path: 'create-assessment', component: VCreateAssessmentComponent},
            {path: 'edit-assessment/:id', component: VEditAssessmentComponent},
            {path: 'view-vulnerabilities/:vulnerabilityAssessmentId', component: VViewVulnerabilitiesComponent},
            {path: 'create-vulnerability/:vulnerabilityAssessmentId', component: VCreateVulnerabilityComponent},
            {path: 'edit-vulnerability/:vulnerabilityAssessmentId/:vulnerabilityAssessmentResultId', component: VEditVulnerabilityComponent},
            {path: 'manage-vulnerability/:vulnerabilityAssessmentId/:vulnerabilityAssessmentResultId', component: VManageVulnerabilityComponent},
            {path: 'view-evidences/:vulnerabilityAssessmentId/:vulnerabilityAssessmentResultId', component: VViewEvidencesComponent},
        ]
    },
    {
        path: 'dashboard',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: HelpDeskComponent
    },
    {
        path: 'asmview',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: AsmComponent,
        children: [
            {path: 'targets', component: ATargetsComponent},
            {path: 'quick-scan-history', component: AQuickScanHistoryComponent},
            {path: 'scan-detail/:scanId', component: AScanFindingsComponent},
            {path: 'vulnerabilities', component: AVulnerabilitiesComponent},
        ]
    },
    {
        path: 'internal-surface',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: InternalSurfaceComponent,
        children: [
            {path: 'infrastructure', component: IInfrastructureComponent},
            {path: 'active-directory', component: IActiveDirectoryComponent},
        ]
    },
    {
        path: 'reports',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: ReportsComponent
    },
    {path: 'blank-page', component: BlankPageComponent},
    {path: 'internal-error', component: InternalErrorComponent},

    {path: '**', component: NotFoundComponent} // This line will remain down from the whole pages component list
];
